import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import File from '@ui/atoms/File';
import * as ExpoDocumentPicker from 'expo-document-picker';
import PropTypes from 'prop-types';
import useTranslate from '@hooks/useTranslate';
import CustomPropTypes from '@utils/CustomPropTypes';
import Pressable from '@ui/atoms/Pressable';
import Ionicons from '@ui/atoms/Ionicons';
import Text from '@ui/atoms/Text';
import { FormattedMessage } from 'react-intl';
import mime from 'mime';

function FilePicker({
  file = undefined,
  onChange,
  loading = false,
  error = false,
  mimeTypes = undefined,
  color = undefined,
  onDelete = undefined,
  disabled = false,
  cloneError, // cette prop sera utilisée par FormControl
}) {
  const { t } = useTranslate();

  const handlePress = async () => {
    try {
      const result = await ExpoDocumentPicker.getDocumentAsync({
        type: mimeTypes,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        // Récupère le blob à partir de l'URI
        const response = await fetch(asset.uri);
        const blob = await response.blob();
        // Utilise window.File pour créer un objet File natif
        const fileObject = new window.File([blob], asset.name, {
          type: asset.type || mime.getType(asset.uri),
        });
        onChange(fileObject);
      }
    } catch (error) {}
  };

  return (
    <>
      <File
        label={loading ? t('common.loading') : file?.name ?? t('ui.FilePicker.import')}
        onPress={disabled ? undefined : handlePress}
        error={error}
        color={color}
      />
      {typeof onDelete === 'function' && file && !disabled && (
        <Pressable onPress={() => onDelete(file)} sx={{ flexDirection: 'row', mt: '$2', pl: '$1' }}>
          <Ionicons name="trash" size={20} sx={{ color: '$red.main' }} />
          <Text variant="body2" sx={{ flex: 1, ml: '$2', color: '$red.main' }} numberOfLines={1}>
            <FormattedMessage id="ui.FilePicker.delete" />
          </Text>
        </Pressable>
      )}
    </>
  );
}

FilePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  mimeTypes: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  file: CustomPropTypes.file,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  onDelete: PropTypes.func,
  cloneError: PropTypes.bool, // marqueur explicite
};

FilePicker.defaultProps = {
  cloneError: true,
};

// Crée le composant memoïsé
const MemoFilePicker = React.memo(FilePicker);
// Utilise hoistNonReactStatics pour copier les statiques
hoistNonReactStatics(MemoFilePicker, FilePicker);

MemoFilePicker.displayName = 'FilePicker';
MemoFilePicker.shouldCloneError = true;

export default MemoFilePicker;
